'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import * as Sentry from '@sentry/nextjs';

import { t } from '../../../../i18n';
import Button from '../../../../components/Button/Button';
import { httpPostWithCsrfToken } from '../../../../utils/Http';
import {
    COMMENTS_URL,
    SELECTION_FIELDS,
    useReportComments,
} from '../ReportCommentsProvider';
import Byline from './Byline';
import Excerpt from './Excerpt';

import s from './CommentPanel.module.scss';

const Errors = ({ id, messages }) =>
    messages.length > 0 && (
        <ul id={id} className={s.Errors}>
            {messages.map((message) => (
                <li key={message}>{message}</li>
            ))}
        </ul>
    );

// Writes a comment on the draft's selection. Length limits are the API's to
// check, so they are only in one place.
const DraftComposer = () => {
    const commentId = useId();
    const quoteErrorsId = useId();
    const commentErrorsId = useId();
    const textBoxRef = useRef(null);
    const {
        pageId,
        readerName,
        draft,
        updateDraft,
        discardDraft,
        commentPosted,
    } = useReportComments();
    const [isPosting, setIsPosting] = useState(false);
    const [isQuoteExpanded, setIsQuoteExpanded] = useState(false);

    // Ready to type once "Add comment" is chosen, again after a new selection.
    useEffect(() => {
        textBoxRef.current?.focus();
    }, [draft.selection]);

    // The API's answer, or null when there was none to use.
    const send = async () => {
        try {
            const response = await httpPostWithCsrfToken(COMMENTS_URL, {
                page_id: pageId,
                ...draft.selection,
                comment: draft.comment,
            });
            if (response.status !== 201 && response.status !== 400) {
                throw new Error(`${response.status} ${response.statusText}`);
            }
            return { status: response.status, body: await response.json() };
        } catch (error) {
            console.error('Failed to post report comment:', error);
            Sentry.captureException(error);
            return null;
        }
    };

    // Only a failed request says the comment wasn't posted. Once the API
    // has saved it, trying again would post it twice.
    const post = async (event) => {
        event.preventDefault();
        setIsPosting(true);
        const answer = await send();
        setIsPosting(false);
        if (answer?.status === 201) {
            commentPosted(answer.body);
        } else if (answer?.status === 400) {
            updateDraft({ errors: answer.body.errors, failed: false });
        } else {
            updateDraft({ errors: null, failed: true });
        }
    };

    const errors = draft.errors ?? {};
    // Errors about the selection show by the quote, each message once: the
    // API answers one over-long selection on more than one field.
    const quoteErrors = [
        ...new Set(SELECTION_FIELDS.flatMap((field) => errors[field] ?? [])),
    ];
    const commentErrors = errors.comment ?? [];
    const generalErrors = draft.failed
        ? [t('reportComments.postFailed')]
        : (errors.non_field_errors ?? []);

    return (
        <form className={s.Composer} onSubmit={post}>
            <Byline
                comment={{ author_name: readerName || null, is_own: true }}
            />
            <blockquote
                className={s.Quote}
                aria-describedby={
                    quoteErrors.length ? quoteErrorsId : undefined
                }>
                <Excerpt
                    isExpanded={isQuoteExpanded}
                    onToggle={() => setIsQuoteExpanded(!isQuoteExpanded)}>
                    {draft.selection.selected_text_display}
                </Excerpt>
            </blockquote>
            <Errors id={quoteErrorsId} messages={quoteErrors} />
            <div className={s.Field}>
                <label className={s.Label} htmlFor={commentId}>
                    {t('reportComments.commentLabel')}
                </label>
                <textarea
                    ref={textBoxRef}
                    id={commentId}
                    className={s.TextBox}
                    rows={2}
                    placeholder={t('reportComments.commentPlaceholder')}
                    value={draft.comment}
                    aria-invalid={commentErrors.length > 0}
                    aria-describedby={
                        commentErrors.length ? commentErrorsId : undefined
                    }
                    onChange={(event) =>
                        updateDraft({ comment: event.target.value })
                    }
                />
            </div>
            <Errors id={commentErrorsId} messages={commentErrors} />
            {generalErrors.length > 0 && (
                <p className={s.Errors} role="alert">
                    {generalErrors.join(' ')}
                </p>
            )}
            <div className={s.Actions}>
                <Button
                    type="submit"
                    modifier="Secondary"
                    size="Small"
                    isFullWidth
                    title={t('reportComments.post')}
                    disabled={isPosting}
                />
                <button
                    type="button"
                    className={s.TextButton}
                    onClick={discardDraft}>
                    {t('reportComments.cancel')}
                </button>
            </div>
        </form>
    );
};

export default DraftComposer;
